import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Paper, FormControl,
  Select, MenuItem, Button, IconButton, Autocomplete,
  TextField, Checkbox, FormControlLabel, CircularProgress,
  Snackbar, Alert, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions
} from '@mui/material';
import { ArrowBack, Delete } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar.tsx';
import { useAuth } from '../context/AuthContext.tsx';

const API_BASE = process.env.REACT_APP_API_URL + '/api';


type CareType = '' | 'vpk' | 'zorgkundige';

const RondeDetail: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [naam, setNaam] = useState("");
  const [short, setShort] = useState("");
  const [type, setType] = useState<"vroege" | "late">("vroege");
  const [visible, setVisible] = useState(true);
  const [description, setDescription] = useState("");
  const [careType, setCareType] = useState<CareType>(""); // ⭐ vpk / zorgkundige

  const [nurseList, setNurseList] = useState<{ id: number; naam: string }[]>([]);
  const [selectedNurses, setSelectedNurses] = useState<number[]>([]);

  const [alert, setAlert] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    console.log("[RondeDetail] fetchData start", { id });
    setLoading(true);

    try {
      const token = localStorage.getItem("auth_token");

      if (!token) {
        setAlert({ message: "Geen token gevonden, log opnieuw in.", type: "error" });
        return;
      }

      if (!id) {
        setAlert({ message: "Geen ronde-id gevonden in de URL.", type: "error" });
        return;
      }

      const [tourRes, nurseRes] = await Promise.all([
        fetch(`${API_BASE}/tours/${id}/details`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_BASE}/nurses`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      console.log("[RondeDetail] responses", {
        tourStatus: tourRes.status,
        nurseStatus: nurseRes.status
      });

      if (!tourRes.ok) throw new Error("Tour niet gevonden");
      if (!nurseRes.ok) throw new Error("Nurse data error");

      const tourData = await tourRes.json();
      const nurseData = await nurseRes.json();

      console.log("[RondeDetail] tourData", tourData);

      document.title = `Ronde ${tourData.naam} - Portaal`;

      setNaam(tourData.naam);
      setShort(tourData.short || "");
      setType((tourData.type || "vroege") as "vroege" | "late");
      setDescription(tourData.description || "");
      setVisible(!!tourData.visible);

      // ⭐ verwacht dat backend careType/care_type teruggeeft
      setCareType((tourData.careType || tourData.care_type || "") as CareType);

      setNurseList(
        Array.isArray(nurseData)
          ? nurseData.map((n: any) => ({
              id: n.id,
              naam: n.name
            }))
          : []
      );

      setSelectedNurses(
        Array.isArray(tourData.nurses)
          ? tourData.nurses.map((n: any) => Number(n.id))
          : []
      );
    } catch (err) {
      console.error("[RondeDetail] FOUT in fetchData", err);
      setAlert({
        message:
          err instanceof Error
            ? err.message
            : "Kan tourgegevens niet ophalen!",
        type: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem("auth_token");

      const response = await fetch(`${API_BASE}/tours/${id}`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          naam,
          type,
          short,
          description,
          visible,
          careType,          // ⭐ naar backend sturen
          nurseIds: selectedNurses
        })
      });

      const data = await response.json();

      if (response.ok) {
        setAlert({ message: "Tour succesvol opgeslagen!", type: "success" });
      } else {
        throw new Error(data.message);
      }
    } catch (err) {
      setAlert({ message: "Opslaan mislukt!", type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem("auth_token");

      const response = await fetch(`${API_BASE}/tours/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok)
        return setAlert({
          message: data.message || "Fout bij verwijderen",
          type: "error"
        });

      setAlert({ message: "Ronde succesvol verwijderd!", type: "success" });
      setTimeout(() => navigate("/rondes"), 2000);
    } catch (error) {
      console.error(error);
      setAlert({ message: "Kon ronde niet verwijderen", type: "error" });
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", height: "100vh", justifyContent: "center", alignItems: "center" }}>
        <CircularProgress sx={{ color: "#4aa8b4" }} />
      </Box>
    );
  }

  // kleurpreview op basis van type
  const careColor =
    careType === "vpk"
      ? "#2196f3" // blauw
      : careType === "zorgkundige"
      ? "#ff9800" // oranje
      : "#e0e0e0";

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar open={true} onClose={() => {}} />

      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton onClick={() => navigate("/rondes")}>
                <ArrowBack sx={{ color: "#4aa8b4" }} />
              </IconButton>
              <Typography variant="h4" sx={{ color: '#4aa8b4', ml: 2 }}>
                Ronde Detail
              </Typography>
            </Box>

            {user?.role === 'admin' && (
              <IconButton
                onClick={() => setDeleteDialogOpen(true)}
                sx={{
                  color: '#d32f2f',
                  '&:hover': {
                    backgroundColor: 'rgba(211, 47, 47, 0.1)'
                  }
                }}
              >
                <Delete />
              </IconButton>
            )}
          </Box>

          <Paper sx={{ p: 4 }}>
            <FormControlLabel
              control={<Checkbox checked={visible} onChange={e => setVisible(e.target.checked)} />}
              label="Zichtbaar"
            />

            <Typography sx={{ mt: 2 }}>Naam</Typography>
            <TextField fullWidth value={naam} onChange={e => setNaam(e.target.value)} />

            <Typography sx={{ mt: 2 }}>Short</Typography>
            <TextField fullWidth value={short} onChange={e => setShort(e.target.value)} />

            <Typography sx={{ mt: 2 }}>Type (vroege/late)</Typography>
            <Select fullWidth value={type} onChange={e => setType(e.target.value as any)}>
              <MenuItem value="vroege">Vroege</MenuItem>
              <MenuItem value="late">Late</MenuItem>
            </Select>

            {/* ⭐ NIEUWE DROPDOWN VOOR VPK / ZORGKUNDIGE */}
            <Typography sx={{ mt: 2 }}>Type zorg</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
              <FormControl fullWidth>
                <Select
                  value={careType}
                  onChange={e => setCareType(e.target.value as CareType)}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>Geen specifiek type</em>
                  </MenuItem>
                  <MenuItem value="vpk">Verpleegkundige</MenuItem>
                  <MenuItem value="zorgkundige">Zorgkundige</MenuItem>
                </Select>
              </FormControl>

              {/* kleur preview */}
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  border: '1px solid #ccc',
                  backgroundColor: careColor
                }}
              />
            </Box>

            <Typography sx={{ mt: 2 }}>Beschrijving</Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={description}
              onChange={e => setDescription(e.target.value)}
            />

            <Typography sx={{ mt: 2 }}>Verpleegkundigen</Typography>
            <Autocomplete
              multiple
              options={nurseList}
              getOptionLabel={(option) => option.naam}
              value={nurseList.filter((n) => selectedNurses.includes(n.id))}
              onChange={(e, val) => setSelectedNurses(val.map(v => v.id))}
              renderInput={(params) =>
                <TextField {...params} placeholder="Zoek verpleegkundigen..." />
              }
              sx={{ mt: 1 }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
              <Button variant="outlined" onClick={() => navigate("/rondes")}>Annuleren</Button>
              <Button
                variant="contained"
                disabled={saving}
                onClick={handleSave}
                sx={{ backgroundColor: "#4aa8b4" }}
              >
                {saving ? "Opslaan..." : "Opslaan"}
              </Button>
            </Box>
          </Paper>
        </Container>

        {alert && (
          <Snackbar open autoHideDuration={3000} onClose={() => setAlert(null)}>
            <Alert severity={alert.type}>{alert.message}</Alert>
          </Snackbar>
        )}

        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">
            Ronde verwijderen
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-dialog-description">
              Weet je zeker dat je de ronde <strong>"{naam}"</strong> wilt verwijderen?
              Deze actie kan niet ongedaan worden gemaakt.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              sx={{ color: '#666' }}
            >
              Annuleren
            </Button>
            <Button
              onClick={handleDelete}
              variant="contained"
              sx={{
                backgroundColor: '#d32f2f',
                '&:hover': {
                  backgroundColor: '#b71c1c'
                }
              }}
            >
              Verwijderen
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default RondeDetail;
